import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight } from "@phosphor-icons/react";
import SectionLabel from "../components/SectionLabel";
import {
  SERVICES,
  STATS,
  TESTIMONIALS,
  MEDIA,
  INDUSTRIES,
} from "../lib/constants";
import { fadeUp, staggerContainer, ease } from "../lib/motion";

const Hero = () => (
  <section className="relative min-h-[100svh] flex items-end overflow-hidden">
    <div className="absolute inset-0">
      <img
        src={MEDIA.hero}
        alt=""
        className="w-full h-full object-cover opacity-60"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#050814]/40 via-[#050814]/70 to-[#050814]" />
    </div>

    <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 pb-20 md:pb-28 pt-40 w-full">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
        className="max-w-5xl"
      >
        <motion.div variants={fadeUp} className="flex items-center gap-3 mb-8">
          <span className="h-2 w-2 rounded-full bg-[#d4af37] animate-pulse" />
          <span className="overline">A royal IT software studio • Est. 2017</span>
        </motion.div>

        <motion.h1
          variants={fadeUp}
          className="font-display font-semibold text-white tracking-tighter leading-[0.92]"
          style={{ fontSize: "clamp(48px, 9vw, 144px)" }}
          data-testid="hero-title"
        >
          Engineering <br />
          greater <span className="shimmer italic">possibilities.</span>
        </motion.h1>

        <motion.p
          variants={fadeUp}
          className="mt-10 max-w-xl font-body text-lg md:text-xl text-[#a0abc0] leading-relaxed"
        >
          Evo Technology partners with ambitious enterprises to build custom
          software, cloud platforms and intelligent products — crafted with
          care, delivered with discipline.
        </motion.p>

        <motion.div variants={fadeUp} className="mt-12 flex flex-wrap gap-4">
          <Link
            to="/contact"
            data-testid="hero-primary-cta"
            className="group inline-flex items-center gap-3 bg-[#d4af37] hover:bg-[#f2e3c6] text-[#050814] font-semibold px-8 py-4 rounded-full transition-all duration-300 hover:gap-4"
          >
            Start a project
            <ArrowRight size={18} weight="bold" />
          </Link>
          <Link
            to="/services"
            data-testid="hero-secondary-cta"
            className="inline-flex items-center gap-3 border border-[#d4af37]/60 text-white hover:bg-[#d4af37]/10 font-semibold px-8 py-4 rounded-full transition-all duration-300"
          >
            Explore services
          </Link>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="mt-20 hidden md:flex items-end justify-between gap-12 border-t border-white/10 pt-8"
        >
          {STATS.map((s) => (
            <div key={s.label}>
              <p className="font-display text-4xl md:text-5xl text-white tracking-tighter">
                {s.value}
              </p>
              <p className="overline mt-2 text-[#a0abc0]">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </div>

    {/* Scroll indicator */}
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 1.5, duration: 1 }}
      className="absolute bottom-8 right-8 hidden md:flex items-center gap-2 text-[#a0abc0] font-mono text-xs tracking-widest"
    >
      <span>SCROLL</span>
      <motion.span
        animate={{ y: [0, 6, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        ↓
      </motion.span>
    </motion.div>
  </section>
);

const MarqueeStrip = () => (
  <section className="py-10 border-y border-white/5 bg-[#050814]">
    <Marquee gradient={false} speed={40} pauseOnHover>
      <div className="flex items-center gap-16 mr-16 marquee-track text-2xl md:text-3xl text-white/80">
        {[
          "Innovation",
          "Excellence",
          "Strategy",
          "Cloud",
          "Artificial Intelligence",
          "Cybersecurity",
          "Design",
          "Craft",
        ].map((w, i) => (
          <span key={i} className="flex items-center gap-16">
            {w}
            <span className="text-[#d4af37]">✦</span>
          </span>
        ))}
      </div>
    </Marquee>
  </section>
);

const ServicesPreview = () => (
  <section className="relative py-24 md:py-36 bg-[#050814]">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <SectionLabel number="01" label="Capabilities" />

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease }}
          className="font-display text-4xl md:text-6xl font-medium tracking-tight leading-[1] text-white max-w-2xl"
        >
          A studio of senior engineers, designers and strategists.
        </motion.h2>

        <Link
          to="/services"
          data-testid="services-preview-cta"
          className="inline-flex items-center gap-2 text-[#d4af37] hover:gap-4 transition-all duration-300 font-medium"
        >
          All services <ArrowUpRight size={18} weight="bold" />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {SERVICES.slice(0, 6).map((s, i) => {
          const big = i === 0 || i === 3;
          return (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.7, delay: i * 0.07, ease }}
              className={`${
                big ? "md:col-span-8" : "md:col-span-4"
              } group relative overflow-hidden rounded-2xl border border-white/5 bg-[#0a0f1d] p-8 md:p-10 hover-lift hover:border-[#d4af37]/40`}
              data-testid={`service-card-${s.id}`}
            >
              <div className="flex items-start justify-between">
                <span className="font-mono text-xs text-[#d4af37] tracking-widest">
                  {s.number}
                </span>
                <ArrowUpRight
                  size={20}
                  className="text-[#a0abc0] group-hover:text-[#d4af37] group-hover:-translate-y-1 group-hover:translate-x-1 transition-all duration-500"
                />
              </div>

              <h3 className="font-display text-2xl md:text-3xl mt-10 mb-4 text-white tracking-tight">
                {s.title}
              </h3>
              <p className="text-[#a0abc0] text-sm md:text-base leading-relaxed max-w-md">
                {s.short}
              </p>

              <div
                aria-hidden
                className="absolute -bottom-24 -right-24 w-64 h-64 rounded-full bg-[#d4af37]/0 group-hover:bg-[#d4af37]/5 blur-3xl transition-all duration-700"
              />
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

const ManifestoSection = () => (
  <section className="relative py-24 md:py-36 bg-[#050814] border-t border-white/5">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
      <div className="md:col-span-4">
        <SectionLabel number="02" label="Manifesto" />
      </div>
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.9, ease }}
        className="md:col-span-8"
      >
        <h2 className="font-display text-3xl md:text-5xl leading-[1.1] tracking-tight text-white">
          We believe great software is{" "}
          <span className="text-[#d4af37] italic">quietly excellent</span> —
          built once, built well, and built to be inherited by the team that
          comes next.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-16">
          <p className="text-[#a0abc0] leading-relaxed">
            Founded in 2017 by a small group of engineers and designers tired
            of the consulting status quo, Evo exists for the kind of work that
            doesn't fit into a templated proposal — the messy, the ambitious,
            the long-term.
          </p>
          <p className="text-[#a0abc0] leading-relaxed">
            We work with founders we'd want as friends, on problems that
            deserve the attention of senior people. We staff fewer projects
            than we could, and we ship work we'd put our names on.
          </p>
        </div>
      </motion.div>
    </div>
  </section>
);

const IndustriesMarquee = () => (
  <section className="py-20 bg-[#050814] border-y border-white/5">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 mb-8">
      <p className="overline">Industries we serve</p>
    </div>
    <Marquee gradient={false} speed={30} direction="right">
      <div className="flex items-center gap-12 mr-12 text-xl md:text-2xl font-display tracking-tight text-white/40">
        {INDUSTRIES.concat(INDUSTRIES).map((ind, i) => (
          <span key={i} className="flex items-center gap-12">
            {ind} <span className="text-[#d4af37]/60">/</span>
          </span>
        ))}
      </div>
    </Marquee>
  </section>
);

const Testimonials = () => (
  <section className="relative py-24 md:py-36 bg-[#050814]">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <SectionLabel number="03" label="Voices of our clients" />
      <h2 className="font-display text-3xl md:text-5xl font-medium tracking-tight text-white max-w-3xl mb-16 leading-tight">
        The partnerships we're most proud of usually start with a single email.
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {TESTIMONIALS.map((t, i) => (
          <motion.figure
            key={t.author}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: i * 0.1, ease }}
            className="rounded-2xl border border-white/5 bg-[#0a0f1d] p-8 md:p-10 hover-lift hover:border-[#d4af37]/30"
            data-testid={`testimonial-${i}`}
          >
            <span className="font-display text-5xl text-[#d4af37] leading-none">
              “
            </span>
            <blockquote className="text-[#f8f9fa] text-base md:text-lg leading-relaxed mt-4">
              {t.quote}
            </blockquote>
            <figcaption className="mt-8 pt-6 border-t border-white/5">
              <p className="font-medium text-white">{t.author}</p>
              <p className="text-sm text-[#a0abc0] mt-1">{t.role}</p>
            </figcaption>
          </motion.figure>
        ))}
      </div>
    </div>
  </section>
);

const CTASection = () => (
  <section className="relative py-32 md:py-40 bg-[#0a0f1d] overflow-hidden">
    <div className="absolute inset-0 opacity-30">
      <img src={MEDIA.serviceAbstract} alt="" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0a0f1d] via-[#0a0f1d]/70 to-[#0a0f1d]" />
    </div>
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 1, ease }}
      className="relative max-w-[1400px] mx-auto px-6 md:px-10 text-center"
    >
      <p className="overline mb-6">Begin</p>
      <h2 className="font-display text-4xl md:text-7xl tracking-tighter leading-[0.95] text-white max-w-4xl mx-auto">
        Ready to transform your{" "}
        <span className="shimmer italic">enterprise?</span>
      </h2>
      <p className="mt-8 text-lg text-[#a0abc0] max-w-xl mx-auto">
        Tell us what you're building. We'll respond within 24 hours with senior
        humans, not auto-replies.
      </p>
      <Link
        to="/contact"
        data-testid="bottom-cta"
        className="group inline-flex items-center gap-3 mt-12 bg-[#d4af37] hover:bg-[#f2e3c6] text-[#050814] font-semibold px-10 py-5 rounded-full transition-all duration-300 hover:gap-5"
      >
        Begin the conversation
        <ArrowRight size={20} weight="bold" />
      </Link>
    </motion.div>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <MarqueeStrip />
      <ServicesPreview />
      <ManifestoSection />
      <IndustriesMarquee />
      <Testimonials />
      <CTASection />
    </div>
  );
}
